/**
 * iter-362 — Sponsor Analytics Dashboard.
 *
 * Route: /settings/sponsors/:id/analytics — owner/admin only.
 * Shows daily impressions / clicks / leads for one campaign with
 * a 30-day default view and an "All-time" toggle. Uses recharts
 * for the visual bar/line chart and a per-day table underneath.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, BarChart3, Eye, MousePointerClick, Sparkles, Download } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  Tooltip, CartesianGrid, Legend,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGES = [
  { v: '30d', label: 'Last 30 days' },
  { v: 'all', label: 'All time' },
];

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } catch { return iso; }
};

export default function SponsorAnalytics() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('30d');
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axios.get(`${API}/sponsors/${id}/analytics/timeseries?range=${range}`, { headers })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [id, range]);

  const totals = data?.totals || {};
  const series = data?.series || [];
  // Chart data uses shortened labels
  const chartData = series.map(s => ({ ...s, label: fmtDate(s.date) }));

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="sponsor-analytics-page">
        <div className="flex items-start justify-between">
          <div>
            <button
              onClick={() => navigate('/settings/sponsors')}
              className="text-xs text-stone-500 hover:text-stone-800 flex items-center gap-1 mb-2"
              data-testid="sponsor-analytics-back"
            >
              <ArrowLeft size={12} /> All campaigns
            </button>
            <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
              <BarChart3 size={20} className="text-amber-600" />
              {data?.brand_name || 'Campaign'} — Analytics
            </h1>
            <p className="text-sm text-stone-500 mt-1">
              Track impressions, clicks and leads to justify renewal pricing.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {RANGES.map(r => (
              <button
                key={r.v}
                onClick={() => setRange(r.v)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                  range === r.v
                    ? 'bg-amber-600 text-white border-amber-600'
                    : 'bg-white text-stone-700 border-stone-200 hover:border-amber-300'
                }`}
                data-testid={`sponsor-analytics-range-${r.v}`}
              >
                {r.label}
              </button>
            ))}
            <Link
              to={`${API}/sponsors/${id}/leads.csv`}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1"
              data-testid="sponsor-analytics-download-leads"
              reloadDocument
            >
              <Download size={12} /> Leads CSV
            </Link>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            icon={<Eye size={16} className="text-blue-600" />}
            label={range === 'all' ? 'Impressions' : 'Impressions (in range)'}
            value={(range === 'all' ? totals.impressions_cumulative : totals.impressions_in_range) || 0}
            sub={range === '30d' ? `${totals.impressions_cumulative || 0} all-time` : null}
            testid="sponsor-kpi-impressions"
          />
          <KpiCard
            icon={<MousePointerClick size={16} className="text-amber-600" />}
            label={range === 'all' ? 'Clicks' : 'Clicks (in range)'}
            value={(range === 'all' ? totals.clicks_all_time : totals.clicks_in_range) || 0}
            sub={range === '30d' ? `${totals.clicks_all_time || 0} all-time` : null}
            testid="sponsor-kpi-clicks"
          />
          <KpiCard
            icon={<Sparkles size={16} className="text-emerald-600" />}
            label={range === 'all' ? 'Leads' : 'Leads (in range)'}
            value={(range === 'all' ? totals.leads_all_time : totals.leads_in_range) || 0}
            sub={range === '30d' ? `${totals.leads_all_time || 0} all-time` : null}
            testid="sponsor-kpi-leads"
          />
          <KpiCard
            icon={<BarChart3 size={16} className="text-stone-600" />}
            label="CTR"
            value={(() => {
              const i = range === 'all' ? totals.impressions_cumulative : totals.impressions_in_range;
              const c = range === 'all' ? totals.clicks_all_time : totals.clicks_in_range;
              return i ? `${((c / i) * 100).toFixed(2)}%` : '—';
            })()}
            testid="sponsor-kpi-ctr"
          />
        </div>

        {/* Chart */}
        <div className="bg-white border border-stone-200 rounded-lg p-4" data-testid="sponsor-analytics-chart">
          <h2 className="text-sm font-semibold text-stone-800 mb-3">Daily Performance</h2>
          {loading ? (
            <div className="h-72 flex items-center justify-center text-stone-400">Loading…</div>
          ) : chartData.length === 0 ? (
            <div className="h-72 flex items-center justify-center text-stone-400">No data in this range</div>
          ) : (
            <ResponsiveContainer width="100%" height={288}>
              <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#78716c' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: '#78716c' }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e7e5e4' }}
                  labelStyle={{ color: '#1c1917', fontWeight: 600 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {/* iter-386u-bu — Themable charts consume `--brand-button` (primary CTA color) */}
                <Bar dataKey="impressions" name="Impressions" fill="var(--brand-button, #93c5fd)" radius={[3, 3, 0, 0]} />
                <Bar dataKey="clicks" name="Clicks" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                <Line type="monotone" dataKey="leads" name="Leads" stroke="var(--brand-button, #059669)" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Per-day table */}
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden" data-testid="sponsor-analytics-table">
          <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-stone-800">Per-day breakdown</h3>
            {data && (
              <Badge variant="secondary" className="bg-stone-100 text-stone-600 text-[10px]">
                {data.start_date} → {data.end_date}
              </Badge>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200 sticky top-0">
                <tr className="text-left text-xs uppercase tracking-wider text-stone-500">
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2 text-right">Impressions</th>
                  <th className="px-4 py-2 text-right">Clicks</th>
                  <th className="px-4 py-2 text-right">CTR</th>
                  <th className="px-4 py-2 text-right">Leads</th>
                </tr>
              </thead>
              <tbody>
                {loading && (<tr><td colSpan={5} className="p-6 text-center text-stone-500">Loading…</td></tr>)}
                {!loading && series.length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-stone-500">No events yet for this range.</td></tr>
                )}
                {[...series].reverse().map((s) => {
                  const ctr = s.impressions ? ((s.clicks / s.impressions) * 100).toFixed(1) : '—';
                  return (
                    <tr key={s.date} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`sponsor-day-row-${s.date}`}>
                      <td className="px-4 py-2 text-stone-700">{fmtDate(s.date)}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs text-blue-700">{s.impressions}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs text-amber-700">{s.clicks}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs text-stone-600">{ctr}{ctr !== '—' ? '%' : ''}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs text-emerald-700 font-semibold">{s.leads}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-[10px] text-stone-400 italic text-center">
          AI Guardrail: Sponsor analytics data is view-only for admins and never fed into any cost, rate, spec or recommendation AI prompt.
        </p>
      </div>
    </Layout>
  );
}

function KpiCard({ icon, label, value, sub, testid }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-stone-500 flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className="text-2xl font-bold text-stone-900 mt-1">{typeof value === 'number' ? value.toLocaleString('en-IN') : value}</div>
      {sub && <div className="text-[10px] text-stone-400 mt-0.5">{sub}</div>}
    </div>
  );
}
